import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Loader2,
  Pencil,
  // Plus,
  Trash2,
} from "lucide-react";
import { formatUSPhone, changeName as nameValidator } from "shared";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// import { UserAvatar } from "@/components/user-avatar";
import { useAppForm } from "@/hooks/useCreateForm";
import { queryClient } from "@/lib/trpcClient";

export const Route = createFileRoute("/_authed/_org/settings/profile")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { trpc } = Route.useRouteContext();
  const {
    data: { user },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());
  const { mutate: sendOTPChallenge } = useMutation(
    trpc.auth.sendOTPChallenge.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: (response) => {
        if (response.identifier.includes("phone")) {
          navigate({
            to: "/settings/change-phone",
          });
        } else {
          navigate({
            to: "/settings/change-email",
          });
        }
      },
    }),
  );

  const { mutate: changeName, isPending } = useMutation(
    trpc.auth.changeName.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async () => {
        await queryClient.refetchQueries(trpc.auth.session.queryOptions());
        toast.success("Name updated");
        nameForm.reset();
      },
    }),
  );
  const defaultValues = {
    first: user?.first || "",
    last: user?.last || "",
  };

  const nameForm = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      changeName(value);
    },
    validators: {
      onSubmit: nameValidator,
    },
  });

  return (
    <div className="flex max-w-5xl flex-col">
      {/* <div className="mb-6 flex items-center gap-6">
        {user && (
          <UserAvatar
            userId={user.id}
            includeName={false}
            className="[&>span]:size-16 [&>span]:rounded-full [&>span]:text-2xl"
          />
        )}

        <div className="flex flex-col gap-2">
          <div className="flex gap-4">
            <Button size="sm">
              <Plus className="size-3.5" />
              Change image
            </Button>

            <Button variant="secondary" size="sm">
              Remove image
            </Button>
          </div>

          <span className="text-neutral-500 text-xs">
            We support PNGs and JPEGs under 2MB
          </span>
        </div>
      </div> */}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          nameForm.handleSubmit(e);
        }}
      >
        <div className="mb-0! flex flex-col gap-4 sm:flex-row sm:gap-4">
          <nameForm.AppField
            children={(field) => (
              <field.TextField
                label="First Name"
                placeholder="Enter your first name"
              />
            )}
            name="first"
          />
          <nameForm.AppField
            children={(field) => (
              <field.TextField
                label="Last Name"
                placeholder="Enter your last name"
              />
            )}
            name="last"
          />
        </div>

        <div className="my-8 h-px w-full bg-border" />

        <div className="flex flex-col gap-4 sm:flex-row sm:gap-4">
          <div className="flex w-full items-end gap-2">
            <div className="flex w-full flex-col space-y-2">
              <Label>Email</Label>
              <Input disabled value={user?.email} />
            </div>

            <Button
              onClick={() => sendOTPChallenge({ type: "email" })}
              size="icon"
              type="button"
              variant={"ghost"}
            >
              <Pencil />
            </Button>
          </div>

          <div className="flex w-full items-end gap-2">
            <div className="flex w-full flex-col space-y-2">
              <Label>Phone Number</Label>
              <Input disabled value={formatUSPhone(user?.phone)} />
            </div>
            <Button
              onClick={() => sendOTPChallenge({ type: "phone" })}
              size="icon"
              type="button"
              variant={"ghost"}
            >
              <Pencil />
            </Button>
          </div>
        </div>

        <nameForm.Subscribe
          children={(state) =>
            !state.isDefaultValue ? (
              <div className="flex flex-row space-x-2 self-end">
                <Button
                  onClick={() => {
                    nameForm.reset();
                  }}
                  type="button"
                  variant={"destructive"}
                >
                  <Trash2 />
                  Cancel
                </Button>

                <Button disabled={isPending} type="submit">
                  {isPending && <Loader2 />}
                  Update Name
                </Button>
              </div>
            ) : null
          }
          selector={(state) => state}
        />
      </form>
    </div>
  );
}
